import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';

import { TenantService } from './tenant.service';
import { TokenService } from '@auth/token.service';
import { LoginService } from '@auth/login.service';
import { KioskContextService } from './kiosk-context.service';
import { LoggingService } from './logging.service';

import { ProductActions } from '@core/state/products';
import { ContainerActions } from '@core/state/containers';
import { PrintKioskActions } from '@core/state/print-kiosks';
import { ProductContainerActions } from '@core/state/product-containers';
import { LabelPrinterActions } from '@core/state/label-printers';
import { NotificationsActions } from '@core/state/notifications/notifications.actions';


@Injectable({ providedIn: 'root' })
export class AuthErrorHandlerService {
  private readonly _store = inject(Store);
  private readonly _router = inject(Router);
  private readonly _tenantService = inject(TenantService);
  private readonly _tokenService = inject(TokenService);
  private readonly _loginService = inject(LoginService);
  private readonly _kioskContextService = inject(KioskContextService);
  private readonly _loggingService = inject(LoggingService);

  /**
   * Handles 401 authentication errors by resetting all application state
   * and redirecting to the appropriate page based on route context.
   */
  handle401Error(): void {
    this._loggingService.debug('[AuthErrorHandlerService] Handling 401 error - resetting all states');

    // Reset all NgRx state stores
    this._store.dispatch(ProductActions.reset());
    this._store.dispatch(ContainerActions.reset());
    this._store.dispatch(PrintKioskActions.reset());
    this._store.dispatch(ProductContainerActions.reset());
    this._store.dispatch(LabelPrinterActions.reset());
    this._store.dispatch(NotificationsActions.reset());

    // Reset authentication state
    this._tokenService.resetCredentials();

    // Reset login state
    this._loginService.reset();

    // Reset kiosk context if applicable
    const currentUrl = this._router.url;
    if (currentUrl.includes('/kiosk')) {
      this._kioskContextService.clearContext();
    }

    // Determine route context and navigate
    // Note: Feature-specific redirect paths are handled by ErrorHandlerService.handleAuthError()
    // This method is called by ErrorHandlerService as a fallback, so we use legacy routing here
    const tenantId = this._tenantService.currentUriTenant() || '';

    // Fallback to legacy route detection for backward compatibility
    if (currentUrl.includes('/kiosk')) {
      // Redirect to kiosk unauthorized page
      this._router.navigate([`/${tenantId}/kiosk/unauthorized`], { replaceUrl: true });
      this._loggingService.debug(`[AuthErrorHandlerService] Redirected to kiosk unauthorized page for tenant: ${tenantId}`);
    } else if (currentUrl.includes('/admin')) {
      // Redirect to admin login page
      this._router.navigate([`/${tenantId}/admin/login`], { replaceUrl: true });
      this._loggingService.debug(`[AuthErrorHandlerService] Redirected to admin login page for tenant: ${tenantId}`);
    } else {
      // Fallback: redirect to default kiosk route
      this._router.navigate([`/${tenantId}/kiosk/unauthorized`], { replaceUrl: true });
      this._loggingService.warn(`[AuthErrorHandlerService] Unknown route context, defaulting to kiosk unauthorized: ${currentUrl}`);
    }
  }
}
